// Contact.js
import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const Contactus = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          maxWidth: 900,
          m: { xs: 2, md: "auto" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: { xs: "auto", md: 620 },
          position: "relative",
        }}
      >
        {/* LEFT PANEL */}
        <Box
          sx={{
            flex: { xs: "none", md: "0 0 38%" },
            background:
              "linear-gradient(135deg, #a8d5ba 0%, #c5e1a5 60%, #dcedc8 100%)",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            minHeight: { xs: 160, md: "auto" },
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -60,
              left: -60,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.18)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -80,
              right: -80,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
            }}
          />

          <Box
            component="img"
            src="/images/lider_new2.png"
            alt="Lider"
            sx={{
              maxWidth: { xs: 180, md: 260 },
              width: "100%",
              objectFit: "contain",
              zIndex: 2,
              filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.15))",
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </Box>

        {/* RIGHT PANEL */}
        <Box
          sx={{
            flex: 1,
            background: "#ffffff",
            p: { xs: 3, md: 5 },
            position: "relative",
            overflowY: "auto",
            maxHeight: { xs: "75vh", md: "auto" },
          }}
        >
          {/* CLOSE BUTTON */}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "#94a3b8",
              backgroundColor: "rgba(0,0,0,0.03)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.08)",
                color: "#334155",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* TABS */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              mb: 4,
              mt: 1,
              "& .MuiTabs-indicator": {
                backgroundColor: "#2196f3",
                height: 3,
                borderRadius: 2,
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                color: "#64748b",
                minHeight: 48,
              },
              "& .Mui-selected": {
                color: "#1976d2 !important",
              },
            }}
          >
            <Tab
              icon={<SupportAgentIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Personal Enquiry"
            />
            <Tab
              icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label="Product Enquiry"
            />
          </Tabs>

          {/* PERSONAL ENQUIRY */}
          {activeTab === 0 && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#1976d2",
                  mb: 0.5,
                }}
              >
                Personal Enquiry 📝
              </Typography>

              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: "0.92rem",
                  mb: 3,
                }}
              >
                Share your queries and we'll get back to you within 24 hours
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
                <TextField
                  label="Full Name"
                  placeholder="John Doe"
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Email Address"
                    placeholder="john@example.com"
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Phone Number"
                    placeholder="1234567890"
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                </Box>

                <TextField
                  label="Your Message"
                  placeholder="Tell us how we can help you..."
                  required
                  fullWidth
                  multiline
                  rows={4}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ alignSelf: "flex-start", mt: 1.5 }}
                      >
                        <ChatBubbleOutlineIcon sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<SendIcon />}
                  sx={sendBtnSx}
                >
                  Send Message
                </Button>

                <PrivacyNote />
              </Box>
            </Box>
          )}

          {/* PRODUCT ENQUIRY */}
          {activeTab === 1 && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#1976d2",
                  mb: 0.5,
                }}
              >
                Product Enquiry 🛒
              </Typography>

              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: "0.92rem",
                  mb: 3,
                }}
              >
                Tell us about the product you're interested in
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
                <TextField
                  label="Full Name"
                  placeholder="John Doe"
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Email Address"
                    placeholder="john@example.com"
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Phone Number"
                    placeholder="1234567890"
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                </Box>

                <TextField
                  label="Product Name"
                  placeholder="e.g. ImpulZ School LMS"
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ShoppingCartOutlinedIcon
                          sx={{ color: "#94a3b8" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                <TextField
                  label="Your Message"
                  placeholder="Tell us what you need..."
                  required
                  fullWidth
                  multiline
                  rows={4}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ alignSelf: "flex-start", mt: 1.5 }}
                      >
                        <ChatBubbleOutlineIcon sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<SendIcon />}
                  sx={sendBtnSx}
                >
                  Send Message
                </Button>

                <PrivacyNote />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

// Privacy note reusable
const PrivacyNote = () => (
  <Box
    sx={{
      mt: 2,
      p: 2,
      borderRadius: "12px",
      background: "rgba(76,175,80,0.06)",
      border: "1px solid rgba(76,175,80,0.25)",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 26 }} />
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.9rem",
          color: "#1e293b",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 16, color: "#4caf50" }} />
        Your information is safe with us
      </Typography>
      <Typography
        sx={{
          fontSize: "0.78rem",
          color: "#64748b",
          letterSpacing: 0.5,
          textTransform: "uppercase",
          mt: 0.3,
        }}
      >
        We respect your privacy and never share your data
      </Typography>
    </Box>
  </Box>
);

// Common TextField style
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    "& fieldset": {
      borderColor: "#e2e8f0",
    },
    "&:hover fieldset": {
      borderColor: "#93c5fd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2196f3",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#64748b",
    fontSize: "0.9rem",
  },
};

// Send button style
const sendBtnSx = {
  mt: 1,
  py: 1.5,
  borderRadius: "10px",
  textTransform: "uppercase",
  fontWeight: 800,
  fontSize: "0.95rem",
  letterSpacing: 1,
  background: "linear-gradient(90deg, #2196f3 0%, #03a9f4 100%)",
  boxShadow: "0 8px 20px rgba(33,150,243,0.35)",
  "&:hover": {
    background: "linear-gradient(90deg, #1976d2 0%, #0288d1 100%)",
  },
};

export default Contactus;